import { Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import { EProductTemplateCreationEventStep } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-creation-event-step.enum';
import { EProductTemplateEventName } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-event-name.enum';
import { ECommerceProductVariantOptionsService } from '@api-ui-app/src/app/e-commerce-stores/services/e-commerce-product-variant-options.service';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { BulkConnectVariantsMappingPayload } from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.reducer';
import { getSelectedStoreId, getStoreType } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { PriceNavigatorAutoSaveService } from '@api-ui-app/src/app/price-navigator/prices/services/price-navigator-auto-save.service';
import {
  PriceNavigatorDraftPrices,
  PriceNavigatorListModel,
} from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { PriceNavigatorProductsMappingService } from '@api-ui-app/src/app/price-navigator/price-navigator-shared/services/price-navigator-products-mapping.service';
import { logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import {
  EProductVariantMappingRequest,
  EProductVariantMappingRequestVariant,
} from '@gelato-api-ui/core/e-commerce/e-product-variant-mapping-request';
import { EProductVariantMappingResponse } from '@gelato-api-ui/core/e-commerce/e-product-variant-mapping-response';
import { EProductWizardMode } from '@gelato-api-ui/core/e-commerce/e-product-wizard-mode.enum';
import { mapResponseToBulkConnectProductVariantsMappingPayload } from '@gelato-api-ui/core/e-commerce/helpers/mapResponseToBulkConnectProductVariantsMappingPayload';
import { ECommerceProductGraphQlService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-graph-ql.service';
import { ECommerceProductVariantsApiService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-variants-api.service';
import { ProductPriceApiService } from '@gelato-api-ui/core/product-prices/services/product-price-api.service';
import { PriceNavigatorGraphQlService } from '@gelato-api-ui/sdk/src/lib/shared/price-sdk/price-navigator-graph-ql.service';
import {
  ShowGeneralErrorNotification,
  ShowNotification,
} from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { RecommendedRetailPriceResponse } from '@product-catalogue/src/lib/product-catalogue/types/product-recommeded-retail-prices';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as actions from './e-commerce-product-wizard.actions';
import { logProductTemplateCreatedEvent } from './e-commerce-product-wizard.actions';
import {
  getDestinationProductId,
  getDestinationProductVariants,
  getIsTemplateMode,
  getMode,
  getProductDraftPrices,
  getSelectedStore,
  getStoreProductVariantsCollection,
} from './e-commerce-product-wizard.selector';
import { EStoreType } from '@gelato-api-ui/core/e-commerce/e-store-type.enum';

@Injectable()
export class ECommerceProductWizardEffects {
  draftPrices$: Observable<PriceNavigatorDraftPrices[]> = this.store$.select(getProductDraftPrices);

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly eCommerceProductVariantsApiService: ECommerceProductVariantsApiService,
    private readonly productPriceApiService: ProductPriceApiService,
    private readonly priceNavigatorGraphQlService: PriceNavigatorGraphQlService,
    private readonly eCommerceProductGraphQlService: ECommerceProductGraphQlService,
    private readonly eCommerceProductVariantOptionsService: ECommerceProductVariantOptionsService,
    private readonly priceNavigatorProductsMappingService: PriceNavigatorProductsMappingService,
    private readonly priceNavigatorAutoSaveService: PriceNavigatorAutoSaveService,
  ) {}

  updateSelectedProductAndVariantOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UpdateSelectedProductAndVariantOptions>(
        actions.ECommerceProductWizardActionTypes.UpdateSelectedProductAndVariantOptions,
      ),
      withLatestFrom(this.store$.select(getStoreProductVariantsCollection), this.store$.select(getMode)),
      filter(([, , mode]) =>
        [
          EProductWizardMode.CREATE_PRODUCT,
          EProductWizardMode.CREATE_PRODUCT_TEMPLATE,
          EProductWizardMode.EDIT_PRODUCT_TEMPLATE,
          EProductWizardMode.EDIT_DESIGNS,
          EProductWizardMode.EDIT_DRAFT,
        ].includes(mode),
      ),
      map(([, variantsCollection]) => {
        const usedOptions = this.eCommerceProductVariantOptionsService.extractUsedOptionsFromVariants(
          Object.values(variantsCollection),
        );

        return new actions.SetStoreProductVariantOptions(usedOptions);
      }),
    ),
  );

  loadDefaultBulkConnectProductVariantMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadBulkConnectDefaultProductVariantMappingStart),
      withLatestFrom(
        this.store$.select(getSelectedClientId),
        this.store$.select(getSelectedStoreId),
        this.store$.select(getStoreProductVariantsCollection),
        this.store$.select(getDestinationProductId),
        this.store$.select(getDestinationProductVariants),
      ),
      switchMap(
        ([
          ,
          clientId,
          selectedStoreId,
          storeProductVariantsCollection,
          destinationProductId,
          destinationProductVariants,
        ]) => {
          const variants: EProductVariantMappingRequestVariant[] = Object.values(storeProductVariantsCollection).map(
            (loopProductVariant: EProductVariant): EProductVariantMappingRequestVariant => ({
              id: loopProductVariant.productUid,
              title: loopProductVariant.title,
              variantOptions: loopProductVariant.variantOptions,
            }),
          );

          const request: EProductVariantMappingRequest = { clientId, variants, productId: destinationProductId };

          return this.eCommerceProductVariantsApiService.getMapping(selectedStoreId, request).pipe(
            catchError(() => {
              this.store$.dispatch(actions.loadBulkConnectDefaultProductVariantMappingFailure());
              return of(null);
            }),
            filter((response: EProductVariantMappingResponse): boolean => Boolean(response)),
            map(
              (response: EProductVariantMappingResponse): BulkConnectVariantsMappingPayload =>
                mapResponseToBulkConnectProductVariantsMappingPayload(response, destinationProductVariants),
            ),
            map((mapping: BulkConnectVariantsMappingPayload) =>
              actions.loadBulkConnectDefaultProductVariantMappingSuccess({ payload: mapping }),
            ),
          );
        },
      ),
    ),
  );

  saveProductTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveProductTemplateSuccess),
      switchMap(action => {
        const nextActions: Action[] = [
          new ShowNotification({
            message: this.translateService.instant('txt_product_save_as_template_success_message'),
            type: 'success',
          }),
        ];

        if (action.isNewTemplate) {
          nextActions.push(logProductTemplateCreatedEvent());
        }

        return nextActions;
      }),
    ),
  );

  createProductTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createProductTemplateSuccess),
      switchMap(() => [
        new ShowNotification({
          message: this.translateService.instant('txt_product_template_creation_success_message'),
          type: 'success',
        }),
        logProductTemplateCreatedEvent(),
      ]),
    ),
  );

  logProductTemplateCreatedEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.logProductTemplateCreatedEvent),
        withLatestFrom(this.store$.select(getIsTemplateMode), this.store$.select(getStoreType)),
        tap(([, isTemplateMode, storeType]) => {
          logEventV2(EProductTemplateEventName.CREATED, {
            step: isTemplateMode
              ? EProductTemplateCreationEventStep.STANDALONE_TEMPLATE
              : EProductTemplateCreationEventStep.PRODUCT_CREATION,
            ecomPlatform: storeType,
          });
        }),
      ),
    { dispatch: false },
  );

  // todo: will be unified after new shared Prices table will be implemented and integrated in the flow
  fetchRecommendedRetailPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchRecommendedRetailPrices),
      switchMap(({ productUids, countries }) => {
        return this.productPriceApiService.getRecommendedPrices(productUids, countries);
      }),
      map(({ recommendedRetailPrices }: RecommendedRetailPriceResponse) => {
        return actions.fetchRecommendedRetailPricesSuccess({ recommendedPrices: recommendedRetailPrices });
      }),
      catchError(() => {
        this.store$.dispatch(actions.fetchRecommendedRetailPricesFailure());
        return of(null);
      }),
    ),
  );

  loadProductPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductPrices),
      withLatestFrom(this.store$.select(getSelectedStore)),
      switchMap(
        ([
          {
            payload: { productId, storeId, clientId, country, currency, unpublishedVariantsUids },
          },
          store,
        ]) => {
          const publishedPrices$ = this.priceNavigatorGraphQlService.fetchECommProductsPrices({
            productIds: [productId],
            storeIds: [storeId],
            country,
            currency,
            clientId,
            storeTypes: store.type === EStoreType.TEMPLATE ? [EStoreType.TEMPLATE] : [],
          });
          let unpublishedPrices$: Observable<PriceNavigatorListModel> = of({
            unpublishedProducts: { results: [] },
            stores: [],
          });

          if (unpublishedVariantsUids?.length) {
            // Getting not existing variants prices. E.g. when we add new variants to the product
            unpublishedPrices$ = this.fetchUnpublishedPrices({
              clientId,
              productId,
              storeId,
              country,
              currency,
              storeType: null,
              unpublishedVariantsUids,
            });
          }

          return combineLatest([
            publishedPrices$,
            unpublishedPrices$,
            this.store$.select(getStoreProductVariantsCollection),
          ]).pipe(
            map(([publishedList, unpublishedList, variantsCollection]) =>
              this.priceNavigatorProductsMappingService.mergeEcommProductsListWithUnpublishedListData(
                publishedList,
                unpublishedList,
                variantsCollection,
              ),
            ),
            switchMap(data =>
              combineLatest([
                of(data),
                this.priceNavigatorProductsMappingService.getCurrencyRateService(data.productPrices, currency),
              ]),
            ),
            mergeMap(([data, selectedCurrencyToStoreRate]) => [
              actions.loadProductPricesSuccess({ payload: data.productPrices }),
              actions.setDraftProductPrices({
                payload: this.priceNavigatorProductsMappingService.getInitialEcommProductDraftPrices(
                  data.productPrices,
                  currency,
                  selectedCurrencyToStoreRate,
                ),
              }),
            ]),
            catchError(() => {
              this.store$.dispatch(new ShowGeneralErrorNotification());
              return of(null);
            }),
          );
        },
      ),
    ),
  );

  loadUnpublishedProductPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadUnpublishedProductPrices),
      switchMap(({ payload }) =>
        this.fetchUnpublishedPrices(payload).pipe(
          map(({ unpublishedProducts, stores }) =>
            this.priceNavigatorProductsMappingService.getEcommProductsListData({
              products: unpublishedProducts,
              stores,
            }),
          ),
          switchMap(data =>
            combineLatest([
              of(data),
              this.priceNavigatorProductsMappingService.getCurrencyRateService(data.productPrices, payload.currency),
            ]),
          ),
          mergeMap(([data, selectedCurrencyToStoreRate]) => [
            actions.loadProductPricesSuccess({ payload: data.productPrices }),
            actions.setDraftProductPrices({
              payload: this.priceNavigatorProductsMappingService.getInitialEcommProductDraftPrices(
                data.productPrices,
                payload.currency,
                selectedCurrencyToStoreRate,
              ),
            }),
          ]),
          catchError(() => {
            this.store$.dispatch(new ShowGeneralErrorNotification());
            return of(null);
          }),
        ),
      ),
    ),
  );

  setDraftProductPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setDraftProductPrices),
      switchMap(({ payload: draftPrices }) => {
        const isDraftPricesToSaveEmpty = draftPrices.some(price => !Boolean(price.variantId));
        const observable$ = !isDraftPricesToSaveEmpty
          ? this.eCommerceProductGraphQlService.savePendingPrices(
              this.priceNavigatorAutoSaveService.getProductVariantsPricesToSaveFromDraft(draftPrices),
            )
          : of([]);

        return observable$.pipe(
          map(() => actions.setDraftProductPricesSuccess({ payload: draftPrices })),
          catchError(() => {
            this.store$.dispatch(new ShowGeneralErrorNotification());
            return of(null);
          }),
        );
      }),
    ),
  );

  discardDraftPrices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.discardDraftProductPrices),
      withLatestFrom(this.draftPrices$),
      switchMap(([, draftPrices]) =>
        this.eCommerceProductGraphQlService.savePendingPrices(
          this.priceNavigatorAutoSaveService.getProductVariantsPricesToSaveFromDraft(draftPrices, true),
        ),
      ),
      map(() => actions.discardDraftProductPricesSuccess()),
    );
  });

  showGeneralErrorNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveProductTemplateFailure, actions.createProductTemplateFailure),
      map(() => new ShowGeneralErrorNotification()),
    ),
  );

  private fetchUnpublishedPrices({
    clientId,
    productId,
    country,
    currency,
    storeId,
    storeType,
    unpublishedVariantsUids,
  }) {
    return this.priceNavigatorGraphQlService.fetchUnpublishedProductsPrices({
      clientId,
      productUids: [{ id: productId, productUids: unpublishedVariantsUids }],
      storeIds: [storeId],
      storeTypes: storeType ? [storeType] : [],
      country,
      currency,
    });
  }
}
